import cn from '@sindresorhus/class-names';

import {DirectionButton} from './DirectionButton';
import styles from './NewPagination.module.scss';
import {PageButton} from './PageButton';
import {PaginatedUrlVariant, type PaginationResponsiveConfig} from './types';
import computePagesToRender from './utils/computePagesToRender';
import makeGetHref from './utils/makeGetHref';

type Props = {
  /* текущая страница, считается от 1 */
  currentPage: number;
  /* полное количество страниц */
  totalPages: number;
  /**
   * урл от которого строить урлы для страниц; для первой страницы будет использован as-is
   */
  basePath: string;
  /**
   * как форматировать урлы, примеры:
   *  - 'page-x': basePath/page-10
   *  - 'page/x': basePath/page/10
   */
  urlVariant: PaginatedUrlVariant;

  /**
   * квери-параметры, если переданы, то будут приклеены ко всем урлам
   */
  searchParams?: URLSearchParams;

  /**
   * настройки отображения по брейкпойнтам
   */
  responsiveConfig: PaginationResponsiveConfig;

  className?: string;

  style?: React.CSSProperties;
};

export const NewPagination = ({
  currentPage,
  totalPages,
  basePath,
  urlVariant,
  responsiveConfig,
  className,
  style,
  searchParams,
}: Props) => {
  if (totalPages <= 1) {
    return null;
  }
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const pages = computePagesToRender({
    currentPage,
    totalPages,
    responsiveConfig,
  });

  const getHref = makeGetHref(basePath, urlVariant, searchParams);

  const {keepNextPrevOnSameLineFromWidth} = responsiveConfig;
  return (
    <nav
      aria-label="Навигация по страницам"
      className={cn(
        className,
        styles.root,
        styles[`keepNavButtonsOn${keepNextPrevOnSameLineFromWidth}`],
        hasNext && hasPrev && styles.withNextAndPrev,
        hasNext && !hasPrev && styles.withNext,
        !hasNext && hasPrev && styles.withPrev,
      )}
      style={style}
      data-testid="pagination"
    >
      {hasPrev && (
        <DirectionButton
          direction="prev"
          href={getHref(currentPage - 1)}
          pageNumber={currentPage - 1}
          className={cn(styles.prev, !hasNext && styles.singleNavButton)}
        />
      )}
      <div className={styles.pages}>
        {pages.map(({pageNumber, isEllipsis, minWidth, maxWidth}) => (
          <PageButton
            key={`${pageNumber}${isEllipsis}${minWidth}${maxWidth}`}
            href={getHref(pageNumber)}
            pageNumber={pageNumber}
            isEllipsis={isEllipsis}
            isCurrent={pageNumber === currentPage}
            className={cn(
              minWidth && styles[`showOn${minWidth}`],
              maxWidth && styles[`hideOn${maxWidth}`],
            )}
          />
        ))}
      </div>
      {hasNext && (
        <DirectionButton
          direction="next"
          href={getHref(currentPage + 1)}
          pageNumber={currentPage + 1}
          className={cn(styles.next, !hasPrev && styles.singleNavButton)}
        />
      )}
    </nav>
  );
};
